import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import Icon from 'components/DesignSystem/Icon';
import { selectUserIsAdmin } from 'redux/reducers/authReducer';
import { onTextChange } from 'helper/controlledField';
import { Category, VideoProduct, VideosGetLast } from 'helper/videos';
import { cssVar } from 'constants/theme';

import FilterBar from './FilterBar';
import CategoryTabs from './CategoryTabs';
import VideoResults from './VideoResults';
import FormSheet from './FormSheet';
import DeleteDialog from './DeleteDialog';
import Toast from './Toast';

import './index.scss';

export default function VideosMobile({
  product,
  viewOnly,
  categories,
  categoryId,
}: {
  product: VideoProduct;
  viewOnly: boolean;
  categories: Category[];
  categoryId: number;
}) {
  const userIsAdmin = useSelector(selectUserIsAdmin);
  const isAdminView = userIsAdmin && !viewOnly;

  const [searchBarText, setSearchBarText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const uploadDate = selectedDate?.toLocaleDateString('pt-BR') || null;

  const [view, setView] = useState<'list' | 'grid'>('list');
  const [refreshKey, setRefreshKey] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [deleting, setDeleting] = useState<VideosGetLast | null>(null);
  const [toast, setToast] = useState('');

  const openCreateForm = () => {
    setEditingId(undefined);
    setFormOpen(true);
  };

  const openEditForm = useCallback((id: number) => {
    setEditingId(id);
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setEditingId(undefined);
  }, []);

  const onSaved = useCallback((message: string) => {
    setFormOpen(false);
    setEditingId(undefined);
    setToast(message);
    setRefreshKey((key) => key + 1);
  }, []);

  const onDeleted = useCallback((message: string) => {
    setDeleting(null);
    setToast(message);
    setRefreshKey((key) => key + 1);
  }, []);

  const closeToast = useCallback(() => setToast(''), []);
  const closeDelete = useCallback(() => setDeleting(null), []);

  return (
    <div className="videos-mobile">
      <FilterBar
        searchValue={searchBarText}
        onSearchChange={onTextChange(setSearchBarText)}
        setSearchQuery={setSearchQuery}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
      />
      <CategoryTabs categories={categories} product={product} activeId={categoryId} />

      <div className="videos-mobile-actions">
        {isAdminView && (
          <button
            type="button"
            className="videos-mobile-add"
            onClick={openCreateForm}
          >
            <span className="videos-mobile-add-icon">
              <Icon iconType="add" color={cssVar('text-on-accent')} size="small" />
            </span>
            Adicionar vídeo
          </button>
        )}
        <div className="videos-mobile-toggle">
          <button
            type="button"
            aria-label="Visualizar em lista"
            className={view === 'list' ? 'active' : ''}
            onClick={() => setView('list')}
          >
            <Icon iconType="list" color={cssVar(view === 'list' ? 'text-strong' : 'text-muted')} />
          </button>
          <button
            type="button"
            aria-label="Visualizar em grade"
            className={view === 'grid' ? 'active' : ''}
            onClick={() => setView('grid')}
          >
            <Icon iconType="grid_view" color={cssVar(view === 'grid' ? 'text-strong' : 'text-muted')} />
          </button>
        </div>
      </div>

      <VideoResults
        key={`${categoryId}-${searchQuery}-${uploadDate}-${refreshKey}`}
        product={product}
        categoryId={categoryId}
        searchQuery={searchQuery}
        uploadDate={uploadDate}
        view={view}
        isAdminView={isAdminView}
        onEdit={openEditForm}
        onDelete={setDeleting}
      />

      {isAdminView && (
        <FormSheet
          open={formOpen}
          editingId={editingId}
          product={product}
          dismiss={closeForm}
          onSaved={onSaved}
        />
      )}
      <DeleteDialog
        video={deleting}
        product={product}
        dismiss={closeDelete}
        onDeleted={onDeleted}
      />
      <Toast message={toast} onClose={closeToast} />
    </div>
  );
}
